import React, { Fragment } from 'react';
import { hot } from 'react-hot-loader';
import PropTypes from 'prop-types';
import { Formik, Field } from 'formik';
import { Form, Label, Col, Row, FormGroup, ModalFooter } from 'reactstrap';
import { FormattedMessage } from 'react-intl';
import Select from 'react-select';
import * as Yup from 'yup';
import { get, debounce } from 'lodash';

import { UploadDropzone, ErrorMessage, SubmitButton, LockedModalBody, Spinner } from '../../';
import {
  UPLOAD_EMPLOYEE_DOCUMENT, GET_EMPLOYEES, GET_UPLOAD_URL, GET_COMPANIES,
  SEARCH_EMPLOYEES, LOADING_PROP_TYPE, GET_COMPANY_DOCUMENT_CATEGORIES,
} from '../../../modules';
import FieldBuilder from '../FieldBuilder/FieldBuilder';
import { ROLE_BASE_MANAGER, DROPDOWN_SEARCH_DEBOUNCE_TIME, MAX_EMPLOYEE_DOC_UPLOAD_SIZE_IN_BYTES, MAX_DOC_CATEGORY_LENGTH } from '../../../constants';

const validationSchema = Yup.object().shape({
  employeeId: Yup.string().required('PLEASE_SELECT_AN_EMPLOYEE'),
  companyId: Yup.string().required('PLEASE_SELECT_A_COMPANY'),
  category: Yup.string().max(MAX_DOC_CATEGORY_LENGTH, 'CATEGORY_CANNOT_EXCEED_50_CHARACTERS'),
  title: Yup.string().required('PLEASE_SPECIFY_A_DISPLAY_NAME'),
});

const DocumentUploadForm = ({
  data,
  functions,
  onDrop,
  onUpload,
  hasFile,
  loading,
  onError,
  userRoles,
}) => {
  const {
    companies,
    employees,
    employeeSearchResults,
    groupedCategoryList,
  } = data;
  const {
    getCompanies,
    getEmployees,
    searchEmployees,
    loadCategories,
  } = functions;
  const isLoading =
    loading[UPLOAD_EMPLOYEE_DOCUMENT] === 'loading' ||
    loading[GET_EMPLOYEES] === 'loading' ||
    loading[SEARCH_EMPLOYEES] === 'loading';
  const isSubmitting = loading[UPLOAD_EMPLOYEE_DOCUMENT] === 'loading';
  const {
    employeeId, name, firstName, lastName, employeeCode,
  } = get(window, 'asure.appSettings.selectedEmployee');
  const { companyId, companyName } = get(window, 'asure.appSettings.selectedCompany');
  const employeeLabel = name || (firstName && lastName
    ? `${lastName}, ${firstName}${employeeCode ? ` (${employeeCode})` : ''}`
    : undefined);
  const selectedEmployee = { value: employeeId, label: employeeLabel };
  const selectedCompany = { value: companyId, label: companyName };
  const employeeList = employees ?
    employees.map(employee => ({
      value: employee.id,
      label: `${employee.firstName} ${employee.lastName} (${employee.eeCode})`,
    }))
    :
    [];
  const filteredEmployeeList = employeeSearchResults ?
    employeeSearchResults.map(employee => ({
      value: employee.id,
      label: `${employee.firstName} ${employee.lastName} (${employee.eeCode})`,
    }))
    :
    [];
  const companyList = companies ? companies.map(company => ({ value: company.id, label: company.name })) : [];
  const defaultEmployee = employeeId ? selectedEmployee : undefined;
  const defaultCompany = companyId ? selectedCompany : undefined;
  const togglePrivate = false;
  const debouncedSearchEmployees = debounce((query, selectedCompanyId) => {
    searchEmployees(query, selectedCompanyId);
  }, DROPDOWN_SEARCH_DEBOUNCE_TIME);
  return (
    <Formik
      initialValues={{
        employeeId,
        companyId,
        title: '',
        togglePrivate,

      }}
      validateOnChange
      validateOnBlur
      validationSchema={validationSchema}
    >
      {({
          values,
          errors,
          touched,
        }) => (
          <Fragment>
            <LockedModalBody className="p-4">
              <Form>
                <FieldBuilder
                  formFieldData={
                    [
                      {
                        key: 'company-ee-select',
                        type: 'component',
                        data: {
                          component: (
                            <Row key="company-employee-select">
                              <Col xs={12} sm={6}>
                                <FormGroup>
                                  <Label for="companyId"><FormattedMessage id="COMPANY" /></Label>
                                  <Field
                                    name="companyId"
                                    render={({ form }) => (
                                      <Select
                                        name="companyId"
                                        options={companyList}
                                        classNamePrefix="react-select-container"
                                        defaultValue={defaultCompany}
                                        className={
                                          errors.companyId && touched.companyId
                                            ? 'form-control is-invalid p-0'
                                            : 'react-select-container form-control p-0 mb-0 border-0'
                                        }
                                        onBlur={() => form.setFieldTouched('companyId', true)}
                                        onChange={(option) => {
                                          form.setFieldValue('companyId', option.value);
                                          // Reset employeeId and get a new list of employees for the selected company
                                          form.setFieldValue('employeeId', '');
                                          getEmployees('', option.value);
                                          loadCategories(option.value);
                                        }}
                                        isLoading={loading[GET_COMPANIES] === 'loading'}
                                        isDisabled={loading[UPLOAD_EMPLOYEE_DOCUMENT] === 'loading' || companyId}
                                        placeholder="Select a company"
                                        onMenuScrollToBottom={getCompanies}
                                        components={{ LoadingIndicator: Spinner }}
                                      />
                                    )}
                                  />
                                  <ErrorMessage errorText={errors.companyId} />
                                </FormGroup>
                              </Col>
                              <Col key="employee-select"xs={12} sm={6}>
                                <FormGroup>
                                  <Label for="employeeId"><FormattedMessage id="EMPLOYEE_NAME" /></Label>
                                  <Field
                                    name="employeeId"
                                    render={({ form }) => (
                                      <Select
                                        name="employeeId"
                                        options={filteredEmployeeList.length > 0 ? filteredEmployeeList : employeeList}
                                        classNamePrefix="react-select-container"
                                        defaultValue={defaultEmployee}
                                        value={
                                          defaultEmployee ||
                                          employeeList.filter(employee => employee.value === form.values.employeeId)[0] ||
                                          null
                                        }
                                        className={
                                          errors.employeeId && touched.employeeId
                                            ? 'form-control is-invalid p-0'
                                            : 'react-select-container form-control p-0 mb-0 border-0'
                                        }
                                        onBlur={() => form.setFieldTouched('employeeId', true)}
                                        onChange={option => form.setFieldValue('employeeId', option.value)}
                                        isLoading={loading[GET_EMPLOYEES] === 'loading' || loading[SEARCH_EMPLOYEES] === 'loading'}
                                        isDisabled={loading[UPLOAD_EMPLOYEE_DOCUMENT] === 'loading' || employeeId}
                                        placeholder="Select an employee"
                                        onMenuScrollToBottom={() => getEmployees()}
                                        onInputChange={(currentInput) => { debouncedSearchEmployees(currentInput, form.values.companyId); }}
                                        components={{ LoadingIndicator: Spinner }}
                                      />
                                    )}
                                  />
                                  <ErrorMessage errorText={errors.employeeId} />
                                </FormGroup>
                              </Col>
                            </Row>
                          ),
                        },
                      },
                      {
                        key: 'display-name',
                        type: 'input',
                        data: {
                          colClasses: ['col-6'],
                          name: 'title',
                          label: 'DISPLAY_NAME',
                          placeholder: 'Display name',
                          isLocked: loading[UPLOAD_EMPLOYEE_DOCUMENT] === 'loading',
                        },
                      },
                      {
                        key: 'category',
                        type: 'creatable',
                        data: {
                          dataCy: 'category',
                          colClasses: ['col-6'],
                          name: 'category',
                          label: 'CATEGORY',
                          options: groupedCategoryList,
                          placeholder: 'Select a category',
                          maxLength: MAX_DOC_CATEGORY_LENGTH,
                          customStyles: {
                            singleValue: provided => ({ ...provided, whiteSpace: 'pre !important' }),
                            menu: provided => ({
                              ...provided,
                              whiteSpace: 'pre !important',
                              maxHeight: '20vh',
                              overflow: 'scroll',
                            }),
                            menuList: provided => ({ ...provided, maxHeight: '20vh' }),
                          },
                          onScrollToBottom: () => functions.getCategories(values.companyId),
                          isComponentLoading: loading[GET_COMPANY_DOCUMENT_CATEGORIES] === 'loading',
                          disabled: isSubmitting,
                        },
                      },
                      {
                        type: 'component',
                        key: 'dropzone',
                        data: {
                          component: (
                            <Row key="upload-dropzone">
                              <Col>
                                <FormGroup>
                                  <Label className="mb-1"><FormattedMessage id="DOCUMENT_UPLOAD" /></Label>
                                  <UploadDropzone
                                    onDrop={onDrop}
                                    onError={onError}
                                    disabled={loading[UPLOAD_EMPLOYEE_DOCUMENT] === 'loading'}
                                    fileSizeLimit={MAX_EMPLOYEE_DOC_UPLOAD_SIZE_IN_BYTES}
                                  />
                                </FormGroup>
                              </Col>
                            </Row>
                          ),
                        },
                      },
                      {
                        key: 'document-options',
                        type: 'section',
                        data: {
                          title: 'DOCUMENT_OPTIONS',
                          requiredRole: ROLE_BASE_MANAGER,
                        },
                      },
                      {
                        key: 'toggle-private',
                        type: 'switch',
                        data: {
                          rowClasses: ['col-6'],
                          colClasses: ['p-0'],
                          name: 'togglePrivate',
                          id: 'toggle-private',
                          label: 'Private',
                          requiredRole: ROLE_BASE_MANAGER,
                        },
                      },
                    ]
                  }
                  userRoles={userRoles}
                  errors={errors}
                  touched={touched}
                  locked={false}
                  isSubmitting={loading[UPLOAD_EMPLOYEE_DOCUMENT] === 'loading'}
                />
              </Form>
            </LockedModalBody>
            <ModalFooter>
              <SubmitButton
                onSubmit={onUpload.bind(this, values.title, values.employeeId, values.companyId, values.togglePrivate, values.category)}
                defaultLabel="UPLOAD_DOCUMENT"
                submittingLabel="UPLOADING_DOCUMENT"
                disabled={!values.employeeId || !values.companyId || !hasFile || !values.title || isLoading}
                submitting={loading[UPLOAD_EMPLOYEE_DOCUMENT] === 'loading' || loading[GET_UPLOAD_URL] === 'loading'}
              />
            </ModalFooter>
          </Fragment>
      )}
    </Formik>
  );
};

DocumentUploadForm.propTypes = {
  onDrop: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
  onUpload: PropTypes.func.isRequired,
  hasFile: PropTypes.bool.isRequired,
  loading: LOADING_PROP_TYPE.isRequired,
  userRoles: PropTypes.arrayOf(PropTypes.string).isRequired,
  data: PropTypes.shape({
    employees: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
    })),
    employeeSearchResults: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
    })),
    companies: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
    })),
    groupedCategoryList: PropTypes.arrayOf(PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string,
    })),
  }).isRequired,
  functions: PropTypes.shape({
    getEmployees: PropTypes.func,
    getCompanies: PropTypes.func,
    loadCategories: PropTypes.func,
  }).isRequired,
};

export default hot(module)(DocumentUploadForm);



// WEBPACK FOOTER //
// ./src/components/Forms/DocumentUploadForm/DocumentUploadForm.jsx